import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Avatar, Chip, CircularProgress, Divider,
  Card, CardContent, CardActionArea, Tabs, Tab,
  useMediaQuery, useTheme,
} from '@mui/material';
import ConstructionBanner from '../components/ConstructionBanner';
import Navbar from '../components/Navbar';

const SEASON = 2025;
const SIDEBAR_W = 270;
const NAVBAR_H = 64;
const HEADER_H = 72;

const UNIT_MAP = {
  QB:'offense', RB:'offense', HB:'offense', FB:'offense', WR:'offense',
  TE:'offense', C:'offense', G:'offense', T:'offense', OT:'offense', OG:'offense',
  DE:'defense', DT:'defense', NT:'defense', LB:'defense', OLB:'defense',
  MLB:'defense', ILB:'defense', CB:'defense', S:'defense', FS:'defense',
  SS:'defense', DB:'defense', K:'special', P:'special', LS:'special',
};
const getUnit = (abbr) => UNIT_MAP[abbr?.toUpperCase()] || 'offense';

const sidebarBase = {
  flexShrink: 0,
  backgroundColor: 'transparent',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.4)', borderRadius: 2 },
};

// ── Schedule Sidebar ──────────────────────────────────────────────────────
function ScheduleSidebar({ teamId }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${teamId}/schedule`)
      .then(res => { setGames(res.data?.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [teamId]);

  if (loading) return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.5 }}>
        {SEASON} Schedule
      </Typography>
      {games.map((event, idx) => {
        const comp = event.competitions?.[0];
        const me = comp?.competitors?.find(c => String(c.team?.id) === String(teamId));
        const opp = comp?.competitors?.find(c => String(c.team?.id) !== String(teamId));
        const isHome = me?.homeAway === 'home';
        const done = comp?.status?.type?.completed;
        const won = me?.winner === true;
        const myScore = me?.score?.displayValue ?? me?.score;
        const oppScore = opp?.score?.displayValue ?? opp?.score;
        const tied = done && !won && myScore != null && oppScore != null
          && parseInt(myScore) === parseInt(oppScore) && parseInt(myScore) >= 0;
        const wk = event.week?.number;
        const dateStr = event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
        return (
          <Box key={event.id || idx}>
            <Box sx={{
              display: 'flex', alignItems: 'center', py: 0.65, px: 0.5, borderRadius: 1,
              backgroundColor: done ? (won ? '#0d2b1a' : tied ? '#1a1a0a' : '#2a0d0d') : 'transparent',
              '&:hover': { backgroundColor: 'rgba(245,166,35,0.4)' },
            }}>
              <Typography variant="caption" sx={{ width: 24, color: '#555', fontSize: '0.65rem', fontWeight: 700 }}>
                {wk ? `W${wk}` : ''}
              </Typography>
              <Typography variant="caption" sx={{ width: 18, color: '#777', fontSize: '0.65rem' }}>
                {isHome ? 'vs' : '@'}
              </Typography>
              {opp?.team?.logo
                ? <Avatar src={opp.team.logo} sx={{ width: 20, height: 20, mr: 0.5, borderRadius: '3px' }} />
                : <Box sx={{ width: 20, height: 20, mr: 0.5, backgroundColor: 'rgba(245,166,35,0.4)', borderRadius: '3px' }} />
              }
              <Typography variant="caption" sx={{ flex: 1, fontSize: '0.78rem', color: '#c0d0e0', fontWeight: 500 }}>
                {opp?.team?.abbreviation || '?'}
              </Typography>
              {done ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', color: won ? '#2ea84b' : tied ? '#f5a623' : '#f44336' }}>
                    {won ? 'W' : tied ? 'T' : 'L'}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#aaa' }}>
                    {myScore}-{oppScore}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>{dateStr}</Typography>
              )}
            </Box>
            {idx < games.length - 1 && <Divider sx={{ borderColor: 'rgba(18, 32, 52, 0.80)' }} />}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Roster Sidebar ────────────────────────────────────────────────────────
function RosterSidebar({ teamId }) {
  const [groups, setGroups] = useState({ offense: [], defense: [], special: [] });
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState(0);

  useEffect(() => {
    axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${teamId}/roster`)
      .then(res => {
        const g = { offense: [], defense: [], special: [], other: [] };
        (res.data?.athletes || []).forEach(posGroup => {
          (posGroup.items || []).forEach(player => {
            g[getUnit(player.position?.abbreviation)].push(player);
          });
        });
        setGroups(g);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [teamId]);

  const keys = ['offense', 'defense', 'special'];
  const labels = ['Offense', 'Defense', 'Special Teams'];
  const players = groups[keys[unit]] || [];

  if (loading) return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1 }}>Roster</Typography>
      <Tabs
        value={unit} onChange={(_, v) => setUnit(v)} textColor="inherit" indicatorColor="primary" variant="fullWidth"
        sx={{ minHeight: 32, mb: 0.5, '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none', px: 0.5 } }}
      >
        <Tab label="O" title="Offense" />
        <Tab label="D" title="Defense" />
        <Tab label="ST" title="Special Teams" />
      </Tabs>
      <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1, textAlign: 'center' }}>{labels[unit]}</Typography>
      {!players.length
        ? <Typography variant="caption" sx={{ color: '#555', display: 'block', textAlign: 'center', py: 2 }}>No data</Typography>
        : players.map((p, i) => (
          <Box key={p.id || i}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 0.55, px: 0.5, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(245,166,35,0.4)' } }}>
              {p.headshot?.href
                ? <Avatar src={p.headshot.href} sx={{ width: 22, height: 22, mr: 0.75 }} />
                : <Avatar sx={{ width: 22, height: 22, mr: 0.75, fontSize: '0.55rem', bgcolor: 'rgba(245,166,35,0.4)' }}>{(p.displayName || '?')[0]}</Avatar>
              }
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, fontSize: '0.77rem', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {p.shortName || p.displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#777', fontSize: '0.67rem', ml: 0.5 }}>{p.position?.abbreviation}</Typography>
              {p.jersey && <Typography variant="caption" sx={{ color: '#555', fontSize: '0.63rem', ml: 0.5, width: 22, textAlign: 'right' }}>#{p.jersey}</Typography>}
            </Box>
            {i < players.length - 1 && <Divider sx={{ borderColor: 'rgba(18, 32, 52, 0.80)' }} />}
          </Box>
        ))
      }
    </Box>
  );
}

// ── Team News Center ──────────────────────────────────────────────────────
function TeamNewsCenter({ teamId, teamName, teamColor }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    axios
      .get('https://site.api.espn.com/apis/site/v2/sports/football/nfl/news?limit=50')
      .then((res) => {
        const all = res.data?.articles || [];
        const filtered = all.filter((article) =>
          (article.categories || []).some((cat) => String(cat.teamId) === String(teamId))
        );
        if (filtered.length) {
          setArticles(filtered.slice(0, 6));
          setFallback(false);
        } else {
          setArticles(all.slice(0, 6));
          setFallback(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setFallback(true);
        setLoading(false);
      });
  }, [teamId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress color="primary" /></Box>;
  if (!articles.length) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ color: '#666' }}>No recent news found.</Typography></Box>;

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>{teamName} News</Typography>
      {fallback && articles.length > 0 && (
        <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 1 }}>
          No team-specific headlines right now — showing league-wide NFL stories instead.
        </Typography>
      )}
      {articles.map((article, i) => {
        const img = article.images?.[0]?.url;
        const date = article.published ? new Date(article.published).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
        const link = article.links?.web?.href || '#';
        return (
          <Card key={i} sx={{ mb: 2, backgroundColor: 'rgba(16, 32, 52, 0.62)', border: '1px solid #1e3448', '&:hover': { borderColor: teamColor }, transition: 'border-color 0.15s' }}>
            <CardActionArea component="a" href={link} target="_blank" rel="noopener noreferrer">
              <Box sx={{ display: 'flex' }}>
                {img && (
                  <Box component="img" src={img} alt="" sx={{ width: 130, height: 90, objectFit: 'cover', flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
                <CardContent sx={{ flex: 1, py: 1.5, px: 2 }}>
                  <Typography variant="caption" sx={{ color: teamColor, fontWeight: 700, fontSize: '0.63rem', letterSpacing: '0.05em' }}>{date}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35, mb: 0.5, mt: 0.25 }}>{article.headline}</Typography>
                  {article.description && (
                    <Typography variant="caption" sx={{ color: '#8aaac0', lineHeight: 1.5 }}>
                      {article.description.slice(0, 130)}{article.description.length > 130 ? '...' : ''}
                    </Typography>
                  )}
                </CardContent>
              </Box>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}

// ── Main TeamPage ─────────────────────────────────────────────────────────
function TeamPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const theme = useTheme();
  const showLeft = useMediaQuery(theme.breakpoints.up('lg'));
  const showRight = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${teamId}`)
      .then(res => setTeam(res.data?.team || null))
      .catch(() => {});
  }, [teamId]);

  const teamColor = team?.color ? `#${team.color}` : '#2ea84b';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent', display: 'flex', flexDirection: 'column' }}>
      <ConstructionBanner />
      <Navbar />

      {/* Team header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${teamColor}28 0%, #0a141e 100%)`,
        borderBottom: `2px solid ${teamColor}44`,
        px: 3, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: NAVBAR_H, zIndex: 1100,
        minHeight: HEADER_H, backgroundColor: '#08111a',
      }}>
        {team?.logos?.[0]?.href && (
          <Avatar src={team.logos[0].href} alt={team.displayName} sx={{ width: 52, height: 52, borderRadius: 0 }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {team?.displayName || 'Loading...'}
          </Typography>
          {team?.record?.items?.[0]?.summary && (
            <Typography variant="body2" sx={{ color: '#aaa', mt: 0.25 }}>
              {team.record.items[0].summary?.displayValue ?? team.record.items[0].summary} &middot; {SEASON} Season
            </Typography>
          )}
        </Box>
        <Chip
          label="← All Teams"
          onClick={() => navigate('/nfl')}
          sx={{ cursor: 'pointer', backgroundColor: 'rgba(245,166,35,0.4)', color: '#ccc', '&:hover': { backgroundColor: '#2a3d50' } }}
        />
      </Box>

      {/* Three-column body */}
      <Box sx={{ display: 'flex', flex: 1, alignItems: 'flex-start' }}>
        {showLeft && (
          <Box sx={{ ...sidebarBase, width: SIDEBAR_W, position: 'sticky', top: NAVBAR_H + HEADER_H, maxHeight: `calc(100vh - ${NAVBAR_H + HEADER_H}px)`, overflowY: 'auto', borderRight: '1px solid #1e3448' }}>
            <ScheduleSidebar teamId={teamId} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <TeamNewsCenter teamId={teamId} teamName={team?.displayName} teamColor={teamColor} />
        </Box>
        {showRight && (
          <Box sx={{ ...sidebarBase, width: SIDEBAR_W, position: 'sticky', top: NAVBAR_H + HEADER_H, maxHeight: `calc(100vh - ${NAVBAR_H + HEADER_H}px)`, overflowY: 'auto', borderLeft: '1px solid #1e3448' }}>
            <RosterSidebar teamId={teamId} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default TeamPage;
